// PostHog product analytics — sole boundary around posthog-js.
// posthog-js is loaded lazily via dynamic import() so it never enters the
// initial bundle (keeps first paint fast — see CLAUDE.md performance rule).
//
// Option B: no consent gating yet. Disclosed in the privacy policy.
// TODO: gate behind consent before launch — call optIn()/optOut() from
// CookieConsentBanner.onAccept / onRefuse once the banner is mounted.
import logger from '../utils/logger';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

// Comptes de l'équipe (ids Supabase, séparés par des virgules) : marqués
// is_internal pour pouvoir les exclure des stats dans PostHog.
const INTERNAL_USER_IDS = new Set(
  (import.meta.env.VITE_ANALYTICS_INTERNAL_USER_IDS || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
);

let posthog = null;
let initialized = false;
let initPromise = null;

// Staging et prod partagent la même clé PostHog : chaque événement porte
// app_env pour pouvoir filtrer le staging hors des stats.
export function getAppEnv(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  if (hostname === 'kaiylo.fr' || hostname === 'www.kaiylo.fr') return 'production';
  if (hostname.startsWith('staging.')) return 'staging';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  return 'preview';
}

export async function initAnalytics() {
  if (!POSTHOG_KEY) return; // no key (e.g. local dev) → silent no-op
  if (initialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const mod = await import('posthog-js');
      posthog = mod.default;
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: false, // SPA: we capture pageviews manually on route change
        person_profiles: 'identified_only',
        // L'enregistrement de session PostHog doublait celui de Sentry Replay :
        // deux moteurs rrweb sérialisant tout le DOM en continu. Sur iPhone
        // (limite mémoire stricte par onglet), WebKit tuait le renderer en
        // boucle — « Un problème récurrent est survenu », 307 crashs traqués
        // par renderCrashBeacon, exclusivement iOS. On garde les événements
        // produit ; aucun enregistrement de session n'est actif (le replay
        // Sentry a aussi été retiré — voir lib/sentryConfig.js).
        disable_session_recording: true,
      });
      posthog.register({ app_env: getAppEnv() });
      initialized = true;
    } catch (err) {
      logger.warn('[analytics] init failed', err);
    }
  })();

  return initPromise;
}

export function trackPageview(path) {
  if (!initialized || !posthog) return;
  try {
    posthog.capture('$pageview', path ? { $current_url: path } : undefined);
  } catch (err) {
    logger.warn('[analytics] pageview failed', err);
  }
}

// Événement métier explicite. Propriétés : compteurs et statuts uniquement,
// jamais de nom, e-mail ou contenu saisi par l'utilisateur (pas de PII).
export function trackEvent(name, properties) {
  if (!initialized || !posthog || !name) return;
  try {
    posthog.capture(name, properties);
  } catch (err) {
    logger.warn('[analytics] capture failed', err);
  }
}

export function identifyUser(distinctId, properties = {}) {
  if (!initialized || !posthog || !distinctId) return;
  try {
    posthog.identify(distinctId, { ...properties, is_internal: INTERNAL_USER_IDS.has(distinctId) });
  } catch (err) {
    logger.warn('[analytics] identify failed', err);
  }
}

export function resetAnalytics() {
  if (!initialized || !posthog) return;
  try {
    posthog.reset();
    // reset() efface aussi les super-propriétés : sans ce register, tout ce
    // qui suit la déconnexion partirait sans app_env.
    posthog.register({ app_env: getAppEnv() });
  } catch (err) {
    logger.warn('[analytics] reset failed', err);
  }
}

// Aligne l'identité PostHog sur l'état d'auth ; renvoie l'id à mémoriser pour
// l'appel suivant. reset() uniquement sur une vraie déconnexion (utilisateur
// connecté → null) : au démarrage, user vaut null le temps de restaurer la
// session, et un reset() à ce moment-là effaçait app_env et ouvrait une
// fausse session anonyme (~75 % des événements sans app_env en prod).
export function syncAnalyticsUser(previousUserId, user) {
  if (user?.id) {
    identifyUser(user.id, { role: user.role });
    return user.id;
  }
  if (previousUserId) resetAnalytics();
  return null;
}

export function optIn() {
  if (!initialized || !posthog) return;
  try {
    posthog.opt_in_capturing();
  } catch (err) {
    logger.warn('[analytics] optIn failed', err);
  }
}

export function optOut() {
  if (!initialized || !posthog) return;
  try {
    posthog.opt_out_capturing();
  } catch (err) {
    logger.warn('[analytics] optOut failed', err);
  }
}
